import React from "react";
import {
  Typography,
  Box,
  Avatar,
  IconButton,
  Link,
  Grid,
  Paper,
  Chip,
  Divider
} from '@mui/material';
import {
  Instagram as InstagramIcon,
  Hiking as HikingIcon,
  Restaurant as RestaurantIcon,
  Pets as PetsIcon,
  Public as PublicIcon,
  Favorite as FavoriteIcon,
  Terrain as TerrainIcon
} from '@mui/icons-material';
import nationalParks from '../../data/nationalParks.json';
import texasParks from '../../data/texasParks.json';
import avatarImage from './images/enchanted_rock.png';

const About = () => {
  const visitedNational = nationalParks.filter((park) => park.visited).length;
  const visitedTx = texasParks.filter((park) => park.visited).length;
  const txParkCount = texasParks.length;

  const stats = [
    { label: 'National Parks', value: `${visitedNational} / ${nationalParks.length}`, icon: PublicIcon },
    { label: 'TX Parks', value: `${visitedTx} / ${txParkCount}`, icon: TerrainIcon }
  ];

  const highlights = [
    { icon: HikingIcon, label: 'Trails-first mindset' },
    { icon: RestaurantIcon, label: 'Local eats & coffee' },
    { icon: PetsIcon, label: 'Dog-friendly travels' },
    { icon: PublicIcon, label: 'Road trips with a purpose' }
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: { xs: 6, md: 10 },
        px: { xs: 2, sm: 4 },
        background: 'linear-gradient(180deg, #eef2ff 0%, #f8fafc 55%, #ffffff 100%)'
      }}
    >
      <Box
        sx={{
          maxWidth: 1100,
          mx: 'auto',
          backdropFilter: 'blur(18px)',
          borderRadius: 4,
          boxShadow: '0 24px 80px rgba(15, 23, 42, 0.08)',
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: 'rgba(255, 255, 255, 0.92)'
        }}
      >
        <Box sx={{ position: 'absolute', top: -16, right: -16, width: 180, height: 180, bgcolor: '#dbeafe', borderRadius: '50%', opacity: 0.72 }} />
        <Box sx={{ position: 'absolute', bottom: -16, left: -16, width: 140, height: 140, bgcolor: '#dcfce7', borderRadius: '50%', opacity: 0.65 }} />

        <Box sx={{ position: 'relative', p: { xs: 3, sm: 6 } }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={4}>
              <Avatar
                alt="Tyler and Dayna"
                src={avatarImage}
                loading="lazy"
                sx={{
                  width: { xs: 160, sm: 220 },
                  height: { xs: 160, sm: 220 },
                  mx: { xs: 'auto', md: 0 },
                  border: '4px solid rgba(255,255,255,0.9)',
                  boxShadow: '0 24px 60px rgba(15, 23, 42, 0.18)'
                }}
              />
            </Grid>

            <Grid item xs={12} md={8}>
              <Typography variant="h2" gutterBottom sx={{ color: '#0f172a', fontWeight: 800, fontSize: { xs: '2rem', md: '3rem' } }}>
                About TX Park Hoppers
              </Typography>
              <Typography variant="h6" sx={{ color: '#475569', mb: 2, fontWeight: 500 }}>
                A small adventure team exploring parks, trails, local eats, and good memories.
              </Typography>
              <Typography variant="body1" sx={{ color: '#334155', lineHeight: 1.75, mb: 3 }}>
                We are Tyler and Dayna, a husband and wife team who pack the car, grab our blue heeler, and chase the next scenic park. Outdoor time helps us reset, and every trip becomes a new story.
              </Typography>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label="Texas State Parks" color="success" />
                <Chip label="National Parks" color="primary" />
                <Chip label="Nature-first" color="secondary" />
                <Chip label="Family adventures" sx={{ bgcolor: '#fef3c7', color: '#b45309' }} />
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mt: 5, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
            {stats.map((stat) => {
              const Icon = stat.icon;
              return (
                <Paper key={stat.label} elevation={1} sx={{ p: 3, borderRadius: 3, bgcolor: '#f8fafc', minHeight: 125 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <Icon sx={{ color: '#2563eb', fontSize: 28 }} />
                    <Typography variant="subtitle2" sx={{ color: '#0f172a', fontWeight: 700 }}>
                      {stat.label}
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ color: '#0f172a', fontWeight: 800 }}>
                    {stat.value}
                  </Typography>
                </Paper>
              );
            })}
          </Box>

          <Box sx={{ mt: 5 }}>
            <Typography variant="h4" gutterBottom sx={{ color: '#0f172a', fontWeight: 700 }}>
              What makes it special
            </Typography>
            <Typography variant="body1" sx={{ color: '#475569', lineHeight: 1.75, mb: 3 }}>
              For us, a great trip mixes the best trails with local flavor, a little quiet time, and the feeling that the next destination is just around the bend.
            </Typography>

            <Grid container spacing={2}>
              {highlights.map((item) => {
                const Icon = item.icon;
                return (
                  <Grid item xs={12} sm={6} key={item.label}>
                    <Paper elevation={0} sx={{ display: 'flex', gap: 2, alignItems: 'center', p: 2, borderRadius: 3, bgcolor: '#ffffff' }}>
                      <Box sx={{ width: 44, height: 44, borderRadius: '14px', display: 'grid', placeItems: 'center', bgcolor: '#e0f2fe', color: '#0369a1' }}>
                        <Icon />
                      </Box>
                      <Typography variant="body1" sx={{ color: '#334155', fontWeight: 600 }}>
                        {item.label}
                      </Typography>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Box>

          <Divider sx={{ my: 5, borderColor: '#e2e8f0' }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: '#f1f5f9' }}>
                <Typography variant="subtitle2" sx={{ color: '#475569', mb: 1, fontWeight: 700 }}>
                  Current focus
                </Typography>
                <Typography variant="body2" sx={{ color: '#334155', lineHeight: 1.75 }}>
                  Visiting more hidden Texas parks and planning our next national park road trip.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: '#f1f5f9' }}>
                <Typography variant="subtitle2" sx={{ color: '#475569', mb: 1, fontWeight: 700 }}>
                  Why we share
                </Typography>
                <Typography variant="body2" sx={{ color: '#334155', lineHeight: 1.75 }}>
                  To inspire others to explore parks with curiosity and to celebrate the simple joy of being outdoors together.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: '#f1f5f9' }}>
                <Typography variant="subtitle2" sx={{ color: '#475569', mb: 1, fontWeight: 700 }}>
                Follow along
                </Typography>
                <Typography variant="body2" sx={{ color: '#334155', lineHeight: 1.75 }}>
                  Catch the latest park highlights and travel updates on Instagram at @txparkhoppers.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default About;
